import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CodeEditor from "../common/code-editor";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import {
  getFileData,
  getFileList,
  deleteFiles,
  refreshFiles,
  archiveFiles,
  saveDraft,
  publishDraft,
} from "./ui-widget-service";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";

const UiWidgetView = () => {
  const theme = useTheme();
  const [fileRows, setFileRows] = useState([]);
  const [versionRows, setVersionRows] = useState([]);
  const [selectedTwinTag, setSelectedTwinTag] = useState(null);

  const [leftSelectedRow, setLeftSelectedRow] = useState(null);
  const [rightSelectedRow, setRightSelectedRow] = useState(null);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [metaContent, setMetaContent] = useState("");
  const [scriptContent, setScriptContent] = useState("");
  const { successToast, warningToast, errorToast } = useToast();

  const [currentMetaContent, setCurrentMetaContent] = useState("");
  const [currentScriptContent, setCurrentScriptContent] = useState("");
  const [currentTwinTag, setCurrentTwinTag] = useState("");

  const canSave = metaContent.trim() !== "" && scriptContent.trim() !== "";
  // const [title, setTitle] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(null);
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );
  const [statusLoading, setStatusLoading] = useState(false);
  const [contentTab, setContentTab] = useState(0);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleTag, setDeleTag] = useState("");
  const [archiveTag, setArchiveTag] = useState("");
  const [archiveVersion, setArchiveVersion] = useState("");

  const fetchFileList = async () => {
    setStatusLoading(true);
    try {
      const result = await getFileList(developerAccessToken);

      const resp = result?.items;
      if (Array.isArray(resp) && resp.length > 0) {
        setFileRows(resp);

        if (resp[0]) {
          const targetItem = leftSelectedRow
            ? resp.find(item => item.twinTag === leftSelectedRow.twinTag) ||
              resp[0]
            : resp[0];

          setSelectedTwinTag(targetItem.twinTag);
          const firstItemVersions = [
            ...(targetItem.draftVersion ? [targetItem.draftVersion] : []),
            ...targetItem.activeVersions,
          ];
          const versionsList = firstItemVersions.map(version => ({
            twinTag: targetItem.twinTag,
            version: version,
          }));
          setVersionRows(versionsList);
        }
      } else {
        setLeftSelectedRow(null);
        setRightSelectedRow(null);
        setFileRows([]);
      }
    } catch (error) {
      errorToast("Fetch Error!");
      console.error("Error fetching file list:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchFileList();
  }, []);

  const handleRefreshCache = async () => {
    try {
      const result = await refreshFiles(developerAccessToken);
      if (result) {
        successToast("Refresh Success!");
      } else {
        errorToast("Refresh Error!");
      }
    } catch (error) {
      errorToast("Fresh Error!");
      console.error("Error fetching file list:", error);
    } finally {
      fetchFileList();
    }
  };

  const handleLeftRowClick = row => {
    setSelectedTwinTag(row.twinTag);
    const selectedItem = fileRows.find(item => item.twinTag === row.twinTag);
    if (selectedItem) {
      const versions = [
        ...(selectedItem.draftVersion ? [selectedItem.draftVersion] : []),
        ...selectedItem.activeVersions,
      ];
      const versionsList = versions.map(version => ({
        twinTag: row.twinTag,
        version: version,
      }));
      setVersionRows(versionsList);
      setLeftSelectedRow(row);
      setRightSelectedRow(null);
    }
  };

  const handleRightRowClick = row => {
    setRightSelectedRow(row);
  };

  const clearCurrentContent = () => {
    setCurrentMetaContent("");
    setCurrentScriptContent("");
    setCurrentTwinTag("");
  };

  const clearContent = () => {
    setMetaContent("");
    setScriptContent("");
  };

  const fetchFileContent = async (twinTag, version) => {
    try {
      const resp = await getFileData(twinTag, version, developerAccessToken);
      if (resp) {
        setCurrentTwinTag(twinTag || "");
        setCurrentMetaContent(resp.meta || "");
        setCurrentScriptContent(resp.script || "");
      } else {
        clearCurrentContent();
      }
    } catch (error) {
      console.error("Error fetching file content:", error);
      clearCurrentContent();
    }
  };

  const handleViewFile = async row => {
    await fetchFileContent(selectedTwinTag, row.version);
    setContentDialogOpen(true);
  };

  const handleAddCancel = () => {
    setAddDialogOpen(false);
    clearContent();
    setError(false);
    setErrorMessage("");
    setCurrentStep(1);
  };

  const handleUpsetCancel = () => {
    setContentDialogOpen(false);
    clearCurrentContent();
  };

  const handleUpsetFiles = async () => {
    if (!currentMetaContent.trim() || !currentScriptContent.trim()) {
      handleUpsetCancel();
      errorToast("Content cant not be empty!");
      return;
    }

    setIsRunning("UPSET_FILE");

    try {
      const resp = await saveDraft(
        currentMetaContent,
        currentScriptContent,
        developerAccessToken,
      );

      if (resp) {
        successToast("Save Success!");
        await fetchFileList();
      } else {
        errorToast("Save Error!");
      }
    } catch (error) {
      errorToast("Save Error!");
      console.error("Error uploading files:", error);
    } finally {
      handleUpsetCancel();
      setIsRunning(null);
    }
  };

  const handleAddFiles = async () => {
    if (!metaContent.trim() || !scriptContent.trim()) {
      errorToast("Content cant not be empty!");
      handleAddCancel();
      return;
    }

    setIsRunning("ADD_FILE");

    try {
      const resp = await saveDraft(
        metaContent,
        scriptContent,
        developerAccessToken,
      );

      if (resp) {
        await fetchFileList();
        successToast("Add success!");
      } else {
        errorToast("Add Error!");
      }
    } catch (error) {
      errorToast("Error add files:", error);
      console.error("Error add files:", error);
    } finally {
      setIsRunning(null);
      handleAddCancel();
    }
  };

  const handleArchive = async () => {
    if (!archiveTag && archiveVersion) {
      setConfirmArchiveOpen(false);
      errorToast("File does not exist!");
      return;
    }
    setIsRunning(`ARCH_FILE_${archiveTag}`);
    try {
      const success = await archiveFiles(
        archiveTag,
        archiveVersion,
        developerAccessToken,
      );
      if (success) {
        setFileRows(fileRows.filter(row => row.twinTag !== archiveTag));

        await fetchFileList();
        successToast("Archive Success!");
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast(error);
      console.error("Error Archiving file:", error);
    } finally {
      setIsRunning(null);
      setConfirmArchiveOpen(false);
      setArchiveTag("");
      setArchiveVersion("");
    }
  };

  const handleDelete = async () => {
    if (!deleTag) {
      setConfirmDeleteOpen(false);
      errorToast("File does not exist!");
      return;
    }

    setIsRunning(`DELE_FILE_${deleTag}`);

    try {
      const success = await deleteFiles(deleTag, developerAccessToken);
      console.log(success);
      if (success) {
        setFileRows(fileRows.filter(row => row.twinTag !== deleTag));

        await fetchFileList();
        successToast("Delete Success!");
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast(error);
      console.error("Error deleting file:", error);
    } finally {
      setIsRunning(null);
      setConfirmDeleteOpen(false);
      setDeleTag("");
    }
  };

  const handleContentTabChange = (event, newValue) => {
    setContentTab(newValue);
  };

  const handlePublishDraft = async twinTag => {
    setIsRunning(`PUB_DRAFT_${twinTag}`);
    try {
      const resp = await publishDraft(twinTag, developerAccessToken);
      if (resp) {
        successToast("Publish Success!");
        await fetchFileList();
      }
    } catch (error) {
      errorToast(error);
    } finally {
      setIsRunning(null);
    }
  };

  const leftColumns = [
    {
      field: "twinTag",
      headerName: "Twin Tag",
      flex: 1,
      hide: true,
      width: 150,
    },
  ];

  const rightColumns = [
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      width: 100,
    },

    {
      field: "content",
      headerName: "Content",
      flex: 1,
      width: 80,
      renderCell: params => (
        <Button variant="contained" onClick={() => handleViewFile(params.row)}>
          View
        </Button>
      ),
    },

    {
      field: "action",
      headerName: "Actions",
      description: "Available actions to take.",
      sortable: false,
      flex: 2,
      minWidth: 80,
      renderCell: params => {
        return (
          <>
            {params.row.version === "draft" ? (
              <Box display="flex">
                <Box mr={1}>
                  <Button
                    variant="contained"
                    onClick={e => {
                      handlePublishDraft(selectedTwinTag);
                    }}
                    sx={{
                      pointerEvents: isRunning ? "none" : "auto",
                      opacity: 1,
                      "&.Mui-disabled": {
                        backgroundColor: theme => theme.palette.primary.main,
                        color: theme => theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    Publish
                    {isRunning == `PUB_DRAFT_${selectedTwinTag}` && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={e => {
                      setDeleTag(selectedTwinTag);
                      setConfirmDeleteOpen(true);
                    }}
                    sx={{
                      pointerEvents: isRunning ? "none" : "auto",
                      opacity: 1,
                      "&.Mui-disabled": {
                        backgroundColor: theme => theme.palette.primary.main,
                        color: theme => theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            ) : (
              <></>
            )}
            {params.row.version && params.row.version.startsWith("v") ? (
              <Box display="flex" mr={1}>
                <Box>
                  <Button
                    variant="contained"
                    onClick={e => {
                      // e.stopPropagation();
                      setArchiveTag(selectedTwinTag);
                      setArchiveVersion(params.row.version);
                      setConfirmArchiveOpen(true);
                    }}
                    sx={{
                      pointerEvents: isRunning ? "none" : "auto",
                      opacity: 1,
                      "&.Mui-disabled": {
                        backgroundColor: theme => theme.palette.primary.main,
                        color: theme => theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    Archive
                  </Button>
                </Box>
              </Box>
            ) : (
              <></>
            )}
          </>
        );
      },
    },
  ];

  const CustomNoRowsOverlay = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          zIndex: 1,
          fontSize: "16px",
          color: "gray",
        }}
      >
        <Typography variant="body1">No Data Available</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 2 }}>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row">
              <Button
                variant="contained"
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  mb: 2,
                  pointerEvents: isRunning ? "none" : "auto",
                  opacity: 1,
                  "&.Mui-disabled": {
                    backgroundColor: theme => theme.palette.primary.main,
                    color: theme => theme.palette.primary.contrastText,
                  },
                }}
              >
                Add UI
              </Button>

              <Button
                variant="contained"
                onClick={() => fetchFileList()}
                sx={{
                  mb: 2,
                  ml: 2,
                  pointerEvents: isRunning ? "none" : "auto",
                  opacity: 1,
                  "&.Mui-disabled": {
                    backgroundColor: theme => theme.palette.primary.main,
                    color: theme => theme.palette.primary.contrastText,
                  },
                }}
              >
                reload
              </Button>
              <Button
                variant="contained"
                onClick={() => handleRefreshCache()}
                sx={{
                  mb: 2,
                  ml: 2,
                  pointerEvents: isRunning ? "none" : "auto",
                  opacity: 1,
                  "&.Mui-disabled": {
                    backgroundColor: theme => theme.palette.primary.main,
                    color: theme => theme.palette.primary.contrastText,
                  },
                }}
              >
                refresh cache
              </Button>
            </Stack>
          </Grid>

          {statusLoading ? (
            <LoadingSpinner />
          ) : (
            <Grid container spacing={2} direction="column">
              <Grid item xs={10}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "2%",
                  }}
                >
                  <Box sx={{ width: "28%", minWidth: "150px" }}>
                    <DataGrid
                      rows={fileRows}
                      columns={leftColumns}
                      getRowId={row => row.twinTag}
                      onRowClick={params => handleLeftRowClick(params.row)}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 100,
                          },
                        },
                        columns: {
                          ...leftColumns,
                          columnVisibilityModel: {
                            id: false,
                          },
                        },
                      }}
                      pageSizeOptions={[20, 50, 100]}
                      disableRowSelectionOnClick
                      rowsPerPageOptions={[20]}
                      checkboxSelection={false}
                      getRowClassName={params => {
                        return params.row.twinTag == leftSelectedRow?.twinTag
                          ? "selected-row"
                          : "";
                      }}
                      components={{
                        NoRowsOverlay: CustomNoRowsOverlay,
                      }}
                      autoHeight
                      sx={{
                        "& .MuiDataGrid-root": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-cell": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-cell:focus": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-row.selected-row": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                        "& .MuiDataGrid-row.selected-row .MuiDataGrid-cell": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ width: "70%" }}>
                    <DataGrid
                      rows={versionRows}
                      columns={rightColumns}
                      getRowId={row => `${row.version}`}
                      onRowClick={params => handleRightRowClick(params.row)}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 100,
                          },
                        },
                        columns: {
                          ...rightColumns,
                          columnVisibilityModel: {
                            id: false,
                          },
                        },
                      }}
                      pageSizeOptions={[20, 50, 100]}
                      disableRowSelectionOnClick
                      rowsPerPageOptions={[20]}
                      checkboxSelection={false}
                      getRowClassName={params => {
                        return params.row.version == rightSelectedRow?.version
                          ? "selected-row"
                          : "";
                      }}
                      components={{
                        NoRowsOverlay: CustomNoRowsOverlay,
                      }}
                      autoHeight
                      sx={{
                        "& .MuiDataGrid-root": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-cell": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: theme.palette.background.dataGrid,
                        },
                        "& .MuiDataGrid-cell:focus": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                          outline: "none",
                        },
                        "& .MuiDataGrid-row.selected-row": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                        "& .MuiDataGrid-row.selected-row .MuiDataGrid-cell": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Dialog
          open={addDialogOpen}
          onClose={handleAddCancel}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {currentStep === 1
              ? "Enter File Name"
              : currentStep === 2
              ? "Edit Meta Content"
              : "Edit Script Content"}
            <IconButton
              aria-label="close"
              onClick={handleAddCancel}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {currentStep === 1 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Meta Content
                </Typography>
                <CodeEditor
                  value={metaContent}
                  editable={true}
                  onChange={instance => {
                    const code = instance.getValue();
                    setMetaContent(code);
                  }}
                  height="400px"
                />
              </Box>
            )}
            {currentStep === 2 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Script Content
                </Typography>
                <CodeEditor
                  value={scriptContent}
                  editable={true}
                  onChange={instance => {
                    const code = instance.getValue();
                    setScriptContent(code);
                  }}
                  height="400px"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {currentStep === 1 && (
              <>
                <Button onClick={handleAddCancel}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => setCurrentStep(2)}
                  disabled={metaContent.trim() === ""}
                >
                  Next
                </Button>
              </>
            )}
            {currentStep === 2 && (
              <>
                <Button onClick={handleAddCancel}>Cancel</Button>
                <Button onClick={() => setCurrentStep(1)}>Back</Button>
                <Button
                  variant="contained"
                  onClick={handleAddFiles}
                  disabled={!canSave}
                >
                  Save
                  {isRunning === "ADD_FILE" && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Warning</DialogTitle>
          <DialogContent>
            <Box>Are you sure you want to delete this draft?</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Confirm
              {isRunning === `DELE_FILE_${deleTag}` && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmArchiveOpen}
          onClose={() => setConfirmArchiveOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Warning</DialogTitle>
          <DialogContent>
            <Box>Are you sure you want to Archive this version?</Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmArchiveOpen(false)}
              color="primary"
            >
              Cancel
            </Button>
            <Button onClick={handleArchive} color="error" variant="contained">
              Confirm
              {isRunning === `ARCH_FILE_${archiveTag}` && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={contentDialogOpen}
          onClose={() => handleUpsetCancel()}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            File Content
            <IconButton
              aria-label="close"
              onClick={() => {
                handleUpsetCancel();
              }}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <>
              <Box>
                <h2>Content</h2>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={contentTab}
                      onChange={handleContentTabChange}
                      aria-label="Content Tab"
                    >
                      <Tab label="Meta" />
                      <Tab label="Script" />
                    </Tabs>
                  </Box>
                  {contentTab === 0 && (
                    <Box sx={{ flexGrow: 1 }}>
                      <CodeEditor
                        value={currentMetaContent}
                        editable={true}
                        height="800px"
                        onChange={instance => {
                          const code = instance.getValue();
                          setCurrentMetaContent(code);
                        }}
                      />
                    </Box>
                  )}
                  {contentTab === 1 && (
                    <Box sx={{ flexGrow: 1 }}>
                      <CodeEditor
                        value={currentScriptContent}
                        editable={true}
                        height="800px"
                        onChange={instance => {
                          const code = instance.getValue();
                          setCurrentScriptContent(code);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleUpsetCancel()} color="primary">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                handleUpsetFiles(leftSelectedRow?.twinTag);
              }}
              disabled={!currentScriptContent || !currentMetaContent}
            >
              Save As Draft
              {isRunning === "UPSET_FILE" && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default UiWidgetView;
